/**
 * اسکنِ بارکد/QR با دوربینِ گوشی (v2.148.0) — کاربر: «سریالِ لیبلِ خودِ دستگاه را با دوربینِ گوشی همان‌جا اسکن کنیم».
 *
 * موتور: `BarcodeDetector`ِ خودِ مرورگر (کروم اندروید/دسکتاپ — سریع، بدونِ دانلود)؛ اگر نبود (آیفون/سافاری)
 * کتابخانهٔ ZXing به‌صورتِ تنبل بارگیری می‌شود (چانکِ جدا، فقط وقتی کسی اسکن می‌زند).
 * اگر دوربینِ زنده در دسترس نبود (اجازه رد شد، http، مرورگرِ داخلیِ برنامه‌ها) ⇒ «عکس از لیبل» با دوربینِ گوشی.
 * دوربین فقط روی HTTPS کار می‌کند (سایتِ اصلی HTTPS است).
 *
 * v2.150.0 — حالتِ «متن» (OCR): کاربر: «سریال‌های ما معمولاً بارکدی نیستند؛ اسکنِ متن هم باشد». کادر را روی سریال
 * می‌گیرند و «بخوان» می‌زنند؛ فقط داخلِ کادر خوانده می‌شود و تکه‌های شبیهِ سریال به‌صورتِ دکمه پیشنهاد می‌شوند.
 * حالتِ آخرِ هر فیلد (`modeKey`) یادش می‌ماند.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Grid, Input, Modal, Progress, Segmented, Space, Tag, Typography } from "antd";
import {
  CameraOutlined, CheckOutlined, ReloadOutlined, BulbOutlined, PictureOutlined, BarcodeOutlined,
  FontSizeOutlined, ScanOutlined,
} from "@ant-design/icons";
import { prepareCanvas, recognizeText } from "../utils/ocr";
import { loadOrder, saveOrder } from "../utils/layoutStore";

const { Text } = Typography;
const NATIVE_FORMATS = ["code_128", "code_39", "code_93", "codabar", "ean_13", "ean_8", "itf",
  "upc_a", "upc_e", "qr_code", "data_matrix", "pdf417", "aztec"];

/** ارقامِ فارسی/عربی ⇒ لاتین و حذفِ نویسه‌های کنترلی/فاصلهٔ اضافه */
export const cleanScanned = (s) => String(s || "")
  .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
  .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u001f\u007f]/g, "").trim();

async function nativeDetector() {
  if (typeof window === "undefined" || !("BarcodeDetector" in window)) return null;
  try {
    const supported = await window.BarcodeDetector.getSupportedFormats();
    const formats = NATIVE_FORMATS.filter((f) => supported.includes(f));
    if (!formats.length) return null;
    return new window.BarcodeDetector({ formats });
  } catch { return null; }
}

/** خواندن از یک فایلِ عکس (پشتیبان برای وقتی دوربینِ زنده نیست) */
async function decodeImageFile(file) {
  const det = await nativeDetector();
  if (det) {
    const bmp = await createImageBitmap(file);
    const r = await det.detect(bmp);
    if (r.length) return r[0].rawValue;
  }
  const { BrowserMultiFormatReader } = await import("@zxing/browser");
  const url = URL.createObjectURL(file);
  try {
    const res = await new BrowserMultiFormatReader().decodeFromImageUrl(url);
    return res.getText();
  } finally { URL.revokeObjectURL(url); }
}

/** کادرِ هدف (نسبت به ویدئوی نمایش‌داده‌شده) — متن: پهن و کوتاه، بارکد: مستطیلِ وسط */
const BOX = { barcode: { t: 0.3, x: 0.08 }, text: { t: 0.36, x: 0.05 } };

/** ناحیهٔ کادر را از ویدئوی `object-fit: cover` به مختصاتِ واقعیِ فریم برمی‌گرداند */
function boxInVideo(video, mode) {
  const vw = video.videoWidth; const vh = video.videoHeight;
  const cw = video.clientWidth; const ch = video.clientHeight;
  const sc = Math.max(cw / vw, ch / vh);
  const offX = (vw * sc - cw) / 2; const offY = (vh * sc - ch) / 2;
  const b = BOX[mode];
  const x0 = (cw * b.x + offX) / sc; const y0 = (ch * b.t + offY) / sc;
  const x1 = (cw * (1 - b.x) + offX) / sc; const y1 = (ch * (1 - b.t) + offY) / sc;
  return [Math.max(0, x0), Math.max(0, y0), Math.min(vw, x1) - Math.max(0, x0), Math.min(vh, y1) - Math.max(0, y0)];
}

export default function BarcodeScanner({ open, onClose, onResult, title = "اسکن", modeKey = "default", defaultMode = "barcode" }) {
  const isMobile = !Grid.useBreakpoint().md;
  const [mode, setModeState] = useState(() => loadOrder("scanmode", modeKey) || defaultMode);   // barcode | text
  const modeRef = useRef(mode);
  const [ocr, setOcr] = useState(null);           // { p, label } حینِ خواندن
  const [cands, setCands] = useState([]);         // پیشنهادهای OCR
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const stopRef = useRef(() => {});
  const fileRef = useRef(null);
  const [status, setStatus] = useState("idle");   // idle | starting | scanning | found | error
  const [error, setError] = useState("");
  const [value, setValue] = useState("");
  const [torch, setTorch] = useState(null);       // null = پشتیبانی نمی‌شود
  const [busyImg, setBusyImg] = useState(false);

  const stop = useCallback(() => {
    try { stopRef.current(); } catch { /* */ }
    stopRef.current = () => {};
    (streamRef.current?.getTracks() || []).forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const found = useCallback((raw) => {
    const v = cleanScanned(raw);
    if (!v) return;
    stop();
    setValue(v);
    setStatus("found");
    try { navigator.vibrate?.(80); } catch { /* */ }
  }, [stop]);

  const start = useCallback(async () => {
    stop();
    setValue(""); setError(""); setTorch(null); setCands([]); setOcr(null); setStatus("starting");
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setStatus("error");
      setError("دوربینِ زنده در این صفحه در دسترس نیست (فقط روی https). از «عکس از لیبل» استفاده کنید.");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
    } catch (e) {
      setStatus("error");
      setError(e?.name === "NotAllowedError"
        ? "اجازهٔ دوربین داده نشد. از تنظیماتِ مرورگر اجازهٔ دوربین را برای این سایت روشن کنید، یا «عکس از لیبل» را بزنید."
        : "دوربین پیدا نشد یا در حالِ استفادهٔ برنامهٔ دیگری است. «عکس از لیبل» را امتحان کنید.");
      return;
    }
    streamRef.current = stream;
    // ویدئو داخلِ مودال است و ممکن است هنوز mount نشده باشد
    for (let i = 0; i < 20 && !videoRef.current; i++) await new Promise((r) => setTimeout(r, 50));
    const video = videoRef.current;
    if (!video) { stop(); return; }
    video.srcObject = stream;
    video.setAttribute("playsinline", "true");
    try { await video.play(); } catch { /* سافاری گاهی play را رد می‌کند ولی پخش می‌شود */ }
    const track = stream.getVideoTracks()[0];
    try { if (track?.getCapabilities?.().torch) setTorch(false); } catch { /* */ }
    setStatus("scanning");
    if (modeRef.current === "text") return;      // حالتِ متن: کاربر خودش «بخوان» را می‌زند

    const det = await nativeDetector();
    if (det) {
      let alive = true;
      stopRef.current = () => { alive = false; };
      const tick = async () => {
        if (!alive) return;
        try {
          if (video.readyState >= 2) {
            const r = await det.detect(video);
            if (alive && r.length) { found(r[0].rawValue); return; }
          }
        } catch { /* فریمِ بد */ }
        if (alive) setTimeout(tick, 120);
      };
      tick();
      return;
    }
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromVideoElement(video, (res) => { if (res) found(res.getText()); });
      stopRef.current = () => controls.stop();
    } catch {
      setStatus("error");
      setError("خوانندهٔ بارکد بارگیری نشد. اتصال را بررسی کنید یا سریال را دستی بنویسید.");
    }
  }, [found, stop]);

  useEffect(() => {
    if (open) {
      const m = loadOrder("scanmode", modeKey) || defaultMode;
      modeRef.current = m; setModeState(m);
      start();
    } else { stop(); setStatus("idle"); }
    return stop;
  }, [open]); // eslint-disable-line

  const setMode = (m) => {
    modeRef.current = m; setModeState(m); saveOrder("scanmode", modeKey, m);
    start();
  };

  /** OCR روی یک canvas؛ اگر یک پیشنهادِ واضح بود همان نشانده می‌شود */
  const runOcr = async (canvas) => {
    setOcr({ p: 0, label: "آماده‌سازی" });
    try {
      const r = await recognizeText(canvas, (p, label) => setOcr({ p, label }));
      setOcr(null);
      if (!r.candidates.length) {
        setError("متنِ قابلِ خواندنی پیدا نشد. نزدیک‌تر، صاف و با نورِ کافی بگیرید (چراغ‌قوه)، یا دستی بنویسید.");
        setStatus("ocr-miss");
        return;
      }
      setCands(r.candidates);
      found(r.candidates[0]);
    } catch {
      setOcr(null);
      setError("موتورِ خواندنِ متن بارگیری نشد. اتصال را بررسی کنید و دوباره بزنید.");
      setStatus("ocr-miss");
    }
  };

  const captureText = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const [sx, sy, sw, sh] = boxInVideo(v, "text");
    const canvas = prepareCanvas(v, sx, sy, sw, sh);
    stop();
    runOcr(canvas);
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try { await track.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch { setTorch(null); }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusyImg(true);
    if (modeRef.current === "text") {
      try {
        const bmp = await createImageBitmap(f);
        stop();
        await runOcr(prepareCanvas(bmp, 0, 0, bmp.width, bmp.height));
      } catch { setStatus("error"); setError("عکس باز نشد."); }
      setBusyImg(false);
      return;
    }
    try {
      found(await decodeImageFile(f));
    } catch {
      setStatus("error");
      setError("در عکس بارکدی پیدا نشد. نزدیک‌تر و صاف بگیرید، یا سریال را دستی بنویسید.");
    }
    setBusyImg(false);
  };

  const accept = () => { if (value.trim()) { onResult(cleanScanned(value)); onClose(); } };

  return (
    <Modal open={open} onCancel={onClose} title={title} destroyOnHidden
      width={isMobile ? "100vw" : 520}
      className={isMobile ? "gx-modal-full" : undefined}
      footer={ocr ? null : status === "found" ? (
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button icon={<ReloadOutlined />} onClick={start}>اسکنِ دوباره</Button>
          <Button type="primary" size="large" icon={<CheckOutlined />} onClick={accept}>همین را بنشان</Button>
        </Space>
      ) : (
        <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
          <Button icon={<PictureOutlined />} loading={busyImg} onClick={() => fileRef.current?.click()}>عکس از لیبل</Button>
          {mode === "text" && status === "scanning" && (
            <Button type="primary" size="large" icon={<ScanOutlined />} onClick={captureText}>بخوان</Button>
          )}
          {status === "ocr-miss" && <Button icon={<ReloadOutlined />} onClick={start}>دوباره</Button>}
          <Button onClick={onClose}>انصراف</Button>
        </Space>
      )}>
      <Segmented block value={mode} onChange={setMode} style={{ marginBottom: 10 }} options={[
        { value: "barcode", label: <span><BarcodeOutlined /> بارکد / QR</span> },
        { value: "text", label: <span><FontSizeOutlined /> متن (سریالِ چاپی)</span> },
      ]} />
      {ocr && (
        <div style={{ padding: "24px 8px", textAlign: "center" }}>
          <Progress type="circle" percent={Math.round(ocr.p * 100)} size={90} />
          <div style={{ marginTop: 10 }}><Text>{ocr.label}…</Text></div>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} />
      {status === "found" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Alert type="success" showIcon message={mode === "text"
            ? "خوانده شد — درست را انتخاب یا اصلاح کنید (O/0 و I/1 را چک کنید)" : "خوانده شد — اگر لازم است اصلاح کنید"} />
          {cands.length > 1 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {cands.map((c) => (
                <Tag.CheckableTag key={c} checked={c === value} onChange={() => setValue(c)}
                  style={{ direction: "ltr", fontSize: 15, padding: "4px 10px", border: "1px solid var(--border, #d9d9d9)" }}>
                  {c}
                </Tag.CheckableTag>
              ))}
            </div>
          )}
          <Input size="large" value={value} onChange={(e) => setValue(e.target.value)} onPressEnter={accept}
            style={{ direction: "ltr", fontSize: 18, fontWeight: 600, textAlign: "center" }} />
        </div>
      )}
      {/* ویدئو همیشه mount می‌ماند تا «اسکنِ دوباره» به آن برسد */}
      {(
        <div style={{ display: status === "found" || ocr ? "none" : "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ position: "relative", background: "#000", borderRadius: 10, overflow: "hidden",
            aspectRatio: isMobile ? "3 / 4" : "4 / 3", maxHeight: isMobile ? "62vh" : 360 }}>
            <video ref={videoRef} muted playsInline autoPlay
              style={{ width: "100%", height: "100%", objectFit: "cover",
                display: status === "error" || status === "ocr-miss" ? "none" : "block" }} />
            {status !== "error" && status !== "ocr-miss" && (
              <div style={{ position: "absolute", inset: `${BOX[mode].t * 100}% ${BOX[mode].x * 100}%`,
                border: "3px solid rgba(255,255,255,.9)",
                borderRadius: 10, boxShadow: "0 0 0 100vmax rgba(0,0,0,.35)", pointerEvents: "none" }}>
                {mode === "barcode" && <div style={{ position: "absolute", left: 6, right: 6, top: "50%", height: 2, background: "#ff4d4f" }} />}
              </div>
            )}
            {torch !== null && (
              <Button shape="circle" size="large" icon={<BulbOutlined />} onClick={toggleTorch}
                type={torch ? "primary" : "default"} style={{ position: "absolute", top: 10, insetInlineStart: 10 }} />
            )}
            {(status === "error" || status === "ocr-miss") && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                <CameraOutlined style={{ fontSize: 48, color: "#888" }} />
              </div>
            )}
          </div>
          {status === "error" || status === "ocr-miss"
            ? <Alert type="warning" showIcon message={error} />
            : <Text type="secondary" style={{ textAlign: "center" }}>
                {status === "starting" ? "در حالِ روشن کردنِ دوربین…"
                  : mode === "text" ? "فقط خطِ سریال/کد را داخلِ کادر بگیرید، ثابت نگه دارید و «بخوان» را بزنید"
                    : "بارکد یا QRِ روی لیبلِ دستگاه را داخلِ کادر بگیرید"}
              </Text>}
        </div>
      )}
    </Modal>
  );
}
